import React from 'react'
import { useState, useEffect, useMemo } from 'src/hooks'
import { Flex, Text } from '@sendoutcards/quantum-design-ui'

type FontSizeSettingsProps = {
  currentFontSize: number
  onChangeFontSize: (size: number) => void
}

export const FontSizeSettings = ({
  currentFontSize,
  onChangeFontSize,
}: FontSizeSettingsProps) => {
  const fontSizes = useMemo(() => [12, 16, 18, 24], [])
  const [customValue, setCustomValue] = useState('')

  useEffect(() => {
    if (!fontSizes.includes(currentFontSize)) {
      setCustomValue(currentFontSize.toString())
    }
  }, [currentFontSize, fontSizes])

  const handlePresetClick = (size: number) => {
    onChangeFontSize(size)
    setCustomValue('')
  }

  const handleCustomChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    setCustomValue(value)

    const numValue = parseInt(value)
    if (!isNaN(numValue) && numValue > 0 && numValue <= 100) {
      onChangeFontSize(numValue)
    }
  }

  const handleCustomBlur = () => {
    const numValue = parseInt(customValue)
    if (isNaN(numValue) || numValue <= 0 || numValue > 100) {
      // Reset to last valid value or default
      const validSize = fontSizes.includes(currentFontSize)
        ? currentFontSize
        : fontSizes[0]
      onChangeFontSize(validSize)
      setCustomValue('')
    }
  }

  return (
    <Flex flexDirection="column" outset={{ bottom: 'x_5' }}>
      <Text type="caption" style={labelStyle}>
        Text Size
      </Text>
      <Flex style={textSizeRowStyle}>
        {fontSizes.map(size => (
          <button
            key={size}
            onClick={() => handlePresetClick(size)}
            style={{
              ...sizeButtonStyle,
              ...(currentFontSize === size && !customValue
                ? sizeButtonActiveStyle
                : {}),
            }}
          >
            {size}
          </button>
        ))}

        <Flex
          flexDirection="row"
          alignItems={'center'}
          style={{ marginLeft: 'auto' }}
        >
          <button style={iconButtonStyle}>
            <span style={{ fontSize: currentFontSize, color: '#9CA3AF' }}>
              Aa
            </span>
          </button>
          <input
            type="number"
            value={customValue || currentFontSize}
            onChange={handleCustomChange}
            onBlur={handleCustomBlur}
            min="1"
            max="100"
            style={percentageInputStyle}
          />
          <button style={iconButtonStyle}>
            <span style={{ fontSize: '12px', color: '#9CA3AF' }}>A</span>
          </button>
          <button style={autoButtonStyle}>Auto</button>
        </Flex>
      </Flex>
    </Flex>
  )
}

const labelStyle: React.CSSProperties = {
  fontSize: '12px',
  color: '#6B7280',
  marginBottom: '8px',
  fontWeight: 'normal',
}

const textSizeRowStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '10px',
}

const sizeButtonStyle: React.CSSProperties = {
  width: '48px',
  height: '40px',
  borderRadius: '8px',
  border: 'none',
  fontSize: '14px',
  fontWeight: 500,
  cursor: 'pointer',
  backgroundColor: '#F3F4F6',
  color: '#374151',
  transition: 'background-color 0.2s',
}

const sizeButtonActiveStyle: React.CSSProperties = {
  backgroundColor: '#3B82F6',
  color: 'white',
  boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
}

const iconButtonStyle: React.CSSProperties = {
  width: '40px',
  height: '40px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  border: 'none',
  backgroundColor: 'transparent',
  borderRadius: '8px',
  cursor: 'pointer',
}

const percentageInputStyle: React.CSSProperties = {
  width: '60px',
  fontSize: '14px',
  fontWeight: 600,
  color: '#111827',
  textAlign: 'center',
  border: 'none',
  backgroundColor: 'transparent',
  outline: 'none',
  MozAppearance: 'textfield',
}

const autoButtonStyle: React.CSSProperties = {
  fontSize: '14px',
  fontWeight: 500,
  color: '#3B82F6',
  border: 'none',
  backgroundColor: 'transparent',
  cursor: 'pointer',
  padding: '0 4px',
}
